import isEmpty from 'lodash/isEmpty';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import useTagsHook from '../../../hooks/useTags.hook';
import useJobsHook from '../../../hooks/useJobs.hook';
import useStatusHook from '../../../hooks/useStatus.hook';
import useSourceHook from '../../../hooks/useSource.hook';
import useRegionHook from '../../../hooks/useRegion.hook';
import useDepartmentHook from '../../../hooks/useDepartment.hook';
import useEmployeeHook from '../../../hooks/useEmployee.hook';
import useExperienceHook from '../../../hooks/useExperience.hook';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import { getKeywords, parseSearchParamsRouter } from '../../../services/router.service';

import DateRangeModalComponent from '../../../components/Modal/DateRangeModal.component';
import IconButtonComponent from '../../../components/IconButton.component';
import { StyledCard } from '../../../components/Card.component';
import ButtonComponent from '../../../components/Button.component';
import InputComponent, {
  StyledInputDescription,
} from '../../../components/Input/Input.component';
import SelectComponent, {
  SelectGroupedOption,
} from '../../../components/Input/Select.component';
import { RECORDS_OPTIONS } from '../Report/Params.form';
import { SelectOptionProps } from '../../../components/Input/Select.component';

import CrossIcon from '../../../components/Icons/Cross.icon';
import { RoutesConstant } from '../../../constants/routes.constant';
import { NOT_ASSIGN_ITEM } from '../../../constants/filters.constant';
import { DATE_FORMAT } from '../../../constants/date.contant';
import ChevronRightIcon from '../../../components/Icons/ChevronRight.icon';
import ChevronLeftIcon from '../../../components/Icons/ChevronLeft.icon';
import HRSelect from '../../../components/Input/HRSelect.component';
import RecruiterSelect from '../../../components/Input/RecruiterSelect.component';
import useEnglishLevel from '../../../hooks/useEnglishLevel.hook';

export interface CandidateFiltersFormProps {
  onApply: Function;
  userRoleIds?: string[];
}

const StyledCloseButtonWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

const StyledStatusGroupLabel = styled.span``;

const StyledStatusGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledFilterBlockFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: auto;
  padding-right: 10px;
`;

const StyledFilterBlockSearch = styled.div`
  ${StyledInputDescription} {
    display: none;
  }
`;

export const StyledFilterBlock = styled(StyledCard)<{
  isCollapsed?: boolean;
}>`
  box-sizing: border-box;
  height: 100%;
  display: flex;
  padding: ${({ isCollapsed }) => (isCollapsed ? '0' : '20px 0 20px 20px')};
`;

const StyledFilterWrapper = styled.div<{
  isCollapsed?: boolean;
}>`
  width: 290px;
  display: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'flex')};
  flex-direction: column;
  gap: 15px;
`;

const StyledCollapseLine = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  cursor: pointer;
`;

const DEFAULT_FORM_STATE = {
  keywords: '',
  filter: {
    selectAll: null,
    dateRange: null,
    experience: null,
    englishLevel: null,
    jobs: null,
    status: null,
    source: null,
    hr: null,
    recruiter: null,
    techSpecialist: null,
    referral: null,
    region: null,
    department: null,
    tags: null,
    cvSearch: null,
  },
};
const CandidatesFiltersForm = ({
  onApply,
  userRoleIds,
}: CandidateFiltersFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModalManager();
  const [form, setForm] = useState<any>(DEFAULT_FORM_STATE);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [switches, setSwitches] = useState<Record<string, boolean>>({});
  const { getSelectList: getTagsList, getSelectListByIds: getTagsById } =
    useTagsHook();
  const {
    getSelectList: getJobsList,
    getSelectListByIds: getJobsById,
  } = useJobsHook();
  const {
    getSelectList: getPersonExperienceList,
    getSelectListByIds: getPersonExperienceById,
  } = useExperienceHook();
  const {
    getSelectListByIds: getEnglishLevelById,
    getSelectList: getEnglishLevelList
  } = useEnglishLevel();

  const {
    getSelectList: getEmployeeList,
    getSelectListByIds: getEmployeeById,
  } = useEmployeeHook();
  const {
    getSelectWholeList: getStatusList,
    getSelectListByIds: getStatusById,
  } = useStatusHook();
  const { getSelectList: getSourceList, getSelectListByIds: getSourceById } =
    useSourceHook();
  const { getSelectList: getRegionList, getSelectListByIds: getRegionById } =
    useRegionHook();
  const {
    getSelectList: getDepartmentList,
    getSelectListByIds: getDepartmentById,
  } = useDepartmentHook();

  useEffect(() => {
    setForm((prev: any) => ({
      ...prev,
      keywords: getKeywords(window.location.search) || '',
    }));

    const parsedQueryParams = parseSearchParamsRouter(window.location.search);
    const dateRange = parsedQueryParams['dateRange'];
    const selectAll = parsedQueryParams['selectAll'];
    const jobs = parsedQueryParams['jobs'];
    const status = parsedQueryParams['status'];
    const source = parsedQueryParams['source'];
    const hr = parsedQueryParams['hr'];
    const recruiter = parsedQueryParams['recruiter'];
    const techSpecialist = parsedQueryParams['techSpecialist'];
    const referral = parsedQueryParams['referral'];
    const region = parsedQueryParams['region'];
    const department = parsedQueryParams['department'];
    const experience = parsedQueryParams['experience'];
    const englishLevel = parsedQueryParams['englishLevel'];
    const tags = parsedQueryParams['tags'];
    const cvSearch = parsedQueryParams['cvSearch'];

    console.log(cvSearch);
    // @ts-ignore
    if (dateRange && dateRange.start && dateRange.end) {
      setForm((prev: any) => ({
        ...prev,
        filter: {
          ...prev.filter,
          dateRange,
        },
      }));
    }
    if (jobs && typeof jobs === 'object') {
      const jobsTyped = jobs as { value: unknown[]; switchValue?: boolean };

      if (Array.isArray(jobsTyped.value)) {
        const jobIds = jobsTyped.value.filter((id): id is number => typeof id === 'number');

        getJobsById(jobIds).then((loadedJobs: any) => {
          setForm((prev: any) => ({
            ...prev,
            filter: {
              ...prev.filter,
              jobs: loadedJobs,
            },
          }));

          setSwitches((prev) => ({
            ...prev,
            jobs: jobsTyped.switchValue ?? false,
          }));
        });
      }
    }
    if (cvSearch) {
      setForm((prev: any) => ({
        ...prev,
        filter: {
          ...prev.filter,
          cvSearch: cvSearch || '',
        },
      }));
    }
    if (status && Array.isArray(status) && status.length > 0) {
      getStatusById(status as Array<number>).then((status: any) => {
        setForm((prev: any) => ({
          ...prev,
          filter: {
            ...prev.filter,
            status: isEmpty(status) ? [NOT_ASSIGN_ITEM] : status,
          },
        }));
      });
    }
    if (source && Array.isArray(source) && source.length > 0) {
      getSourceById(source as Array<number>).then((source: any) => {
        setForm((prev: any) => ({
          ...prev,
          filter: {
            ...prev.filter,
            source: isEmpty(source) ? [NOT_ASSIGN_ITEM] : source,
          },
        }));
      });
    }
    if (hr && Array.isArray(hr) && hr.length > 0) {
      getEmployeeById(hr as Array<string>, 'hr').then((hr: any) => {
        setForm((prev: any) => ({
          ...prev,
          filter: {
            ...prev.filter,
            hr: isEmpty(hr) ? [NOT_ASSIGN_ITEM] : hr,
          },
        }));
      });
    }
    if (
      techSpecialist &&
      Array.isArray(techSpecialist) &&
      techSpecialist.length > 0
    ) {
      getEmployeeById(techSpecialist as Array<string>).then(
        (techSpecialist: any) => {
          setForm((prev: any) => ({
            ...prev,
            filter: {
              ...prev.filter,
              techSpecialist: isEmpty(techSpecialist)
                ? NOT_ASSIGN_ITEM
                : techSpecialist,
            },
          }));
        },
      );
    }
    if (referral && Array.isArray(referral) && referral.length > 0) {
      getEmployeeById(referral as Array<string>).then((referral: any) => {
        setForm((prev: any) => ({
          ...prev,
          filter: {
            ...prev.filter,
            referral: isEmpty(referral) ? [NOT_ASSIGN_ITEM] : referral,
          },
        }));
      });
    }
    if (region && Array.isArray(region) && region.length > 0) {
      getRegionById(region as Array<number>).then((region: any) => {
        setForm((prev: any) => ({
          ...prev,
          filter: {
            ...prev.filter,
            region,
          },
        }));
      });
    }
    if (department && Array.isArray(department) && department.length > 0) {
      getDepartmentById(department as Array<number>).then((department: any) => {
        setForm((prev: any) => ({
          ...prev,
          filter: {
            ...prev.filter,
            department: isEmpty(department) ? [NOT_ASSIGN_ITEM] : department,
          },
        }));
      });
    }

    if (experience && Array.isArray(experience) && experience.length > 0) {
      getPersonExperienceById(experience as Array<number>).then(
        (experience: any) => {
          setForm((prev: any) => ({
            ...prev,
            filter: {
              ...prev.filter,
              experience: isEmpty(experience) ? [NOT_ASSIGN_ITEM] : experience,
            },
          }));
        },
      );
    }

    if (
      englishLevel &&
      Array.isArray(englishLevel) &&
      englishLevel.length > 0
    ) {
      getEnglishLevelById(englishLevel as Array<number>).then(
        (englishLevel: any) => {
          setForm((prev: any) => ({
            ...prev,
            filter: {
              ...prev.filter,
              englishLevel: isEmpty(englishLevel)
                ? [NOT_ASSIGN_ITEM]
                : englishLevel,
            },
          }));
        },
      );
    }

    if (selectAll) {
      setForm((prev: any) => ({
        ...prev,
        filter: {
          ...prev.filter,
          selectAll: true,
        },
      }));
    }

    if (tags && Array.isArray(tags) && tags.length > 0) {
      getTagsById(tags as Array<number>).then((tags: SelectOptionProps[]) => {
        setForm((prev: any) => ({
          ...prev,
          filter: {
            ...prev.filter,
            tags,
          },
        }));
      });
    }

    if (recruiter && Array.isArray(recruiter) && recruiter.length > 0) {
      getEmployeeById(recruiter as Array<string>, 'recruiter').then(
        (recruiter: any) => {
          setForm((prev: any) => ({
            ...prev,
            filter: {
              ...prev.filter,
              recruiter: isEmpty(recruiter) ? [NOT_ASSIGN_ITEM] : recruiter,
            },
          }));
        },
      );
    }
  }, []);

  const prepareFilterValue = (form: any, formKey: string) => {
    if (!form[formKey]) {
      return undefined;
    }

    if (Array.isArray(form[formKey])) {
      return form[formKey].map((item: any) => {
        return item.value ? item.value : item;
      });
    }

    return form[formKey].value ? form[formKey].value : form[formKey];
  };

  const handleApplyFilters = useCallback(() => {
    const filters: { keywords: string; filter: Record<string, any> } = {
      keywords: form.keywords,
      filter: {},
    };

    for (const formKey in form.filter) {
      if (form.filter[formKey]) {
        filters.filter[formKey] = prepareFilterValue(form.filter, formKey);
      }
    }
    if (filters.filter.jobs !== undefined) {
      filters.filter.jobs = {
        value: filters.filter.jobs,
        switchValue: switches['jobs'],
      };
    }
    if (filters.filter.cvSearch !== undefined) {
      filters.filter.cvSearch = {
        value: filters.filter.cvSearch,
        switchValue: switches['cvSearch'],
      };
    }
    onApply(filters);
  }, [form, onApply, switches]);


  const handleResetFilters = useCallback(() => {
    const emptyFilters = {
      keywords: '',
      filter: {},
    };

    setForm(DEFAULT_FORM_STATE);

    onApply(emptyFilters);
  }, [onApply]);

  const handleChangeForm =
    (name: string) => (value: number | string | boolean | undefined) => {
      setForm((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    };

  const handleChangeFilters = (name: string) => (value: any) => {
    setForm((prev: any) => ({
      ...prev,
      filter: {
        ...prev.filter,
        [name]: value,
      },
    }));
  };

  const handleSwitchChange = useCallback((key: string, checked: boolean) => {
    handleChangeFilters(key)(checked ? [] : null);

    setSwitches((prev) => ({
      ...prev,
      [key]: checked,
    }));
  }, []);

  const formatStatusGroupLabel = (data: SelectGroupedOption) => (
    <StyledStatusGroup>
      <StyledStatusGroupLabel>{data.label}</StyledStatusGroupLabel>
    </StyledStatusGroup>
  );

  const formatDateRange = useMemo(() => {
    if (!form.filter || !form.filter.dateRange) {
      return undefined;
    }

    return `${dayjs(form.filter.dateRange.start).format(DATE_FORMAT)} - ${dayjs(
      form.filter.dateRange.end,
    ).format(DATE_FORMAT)}`;
  }, [form, form.filter.dateRange]);

  const selectRecordsOptions = useMemo(
    () => RECORDS_OPTIONS.map(obj => ({ ...obj, label: t(obj.label) })),
    [],
  );

  return (
    <StyledFilterBlock isCollapsed={isCollapsed}>
      <StyledFilterWrapper isCollapsed={isCollapsed}>
        <StyledCloseButtonWrapper>
          <IconButtonComponent
            onClick={() => navigate(`${RoutesConstant.CANDIDATES_GROUPS.WAIT}`)}
          >
            <CrossIcon />
          </IconButtonComponent>
        </StyledCloseButtonWrapper>

        <StyledFilterBlockSearch>
          <InputComponent
            placeholder={t('enterNameSurnamePhoneEmail')}
            label={t('Search keywords')}
            value={form.keywords}
            onChange={handleChangeForm('keywords')}
          />
        </StyledFilterBlockSearch>

        <StyledFilterBlockFilters>
          <SelectComponent
            label={t('Records')}
            value={
              form.filter.selectAll
                ? selectRecordsOptions[1]
                : selectRecordsOptions[0]
            }
            options={selectRecordsOptions}
            onChangeOption={({ value }: any) =>
              handleChangeFilters('selectAll')(
                value === selectRecordsOptions[1].value || undefined,
              )
            }
          />
          <HRSelect
            value={form.filter.hr}
            onChange={handleChangeFilters('hr')}
            isMulti
          />
          <RecruiterSelect
            value={form.filter.recruiter}
            onChange={handleChangeFilters('recruiter')}
            isMulti
          />
          <SelectComponent
            label={t('Tech specialist')}
            placeholder={t('Choose tech specialist')}
            isMulti
            value={form.filter.techSpecialist}
            initOptions={[NOT_ASSIGN_ITEM]}
            loadOptions={getEmployeeList}
            onChangeOption={handleChangeFilters('techSpecialist')}
          />
          <SelectComponent
            label={t('Jobs')}
            value={form.filter.jobs}
            initOptions={[NOT_ASSIGN_ITEM]}
            placeholder={t('Choose job(-s)')}
            isMulti
            loadOptions={getJobsList}
            onChangeOption={handleChangeFilters('jobs')}
            isSwitchable={true}
            switchValue={switches['jobs'] || false}
            onSwitchChange={(checked) => handleSwitchChange('jobs', checked)}
            isOptionDisabled={switches['jobs'] && form.filter.jobs?.length >= 3}
            leftSwitchLabel={t('or')}
            rightSwitchLabel={t('and')}
          />
          <InputComponent
            placeholder={t('enterKeywordsByComma')}
            label={t('searchByCV')}
            value={form.filter.cvSearch}
            isSwitchable={true}
            switchValue={switches['cvSearch'] || false}
            onSwitchChange={(checked) => handleSwitchChange('cvSearch', checked)}
            leftSwitchLabel = {t('or')}
            rightSwitchLabel  = {t('and')}
            onChange={handleChangeFilters('cvSearch')}
          />
          <SelectComponent
            label={t('Status')}
            isMulti
            value={form.filter.status}
            initOptions={[NOT_ASSIGN_ITEM]}
            placeholder={t('Choose status')}
            formatGroupLabel={formatStatusGroupLabel}
            loadOptions={getStatusList}
            onChangeOption={handleChangeFilters('status')}
            selectGroup
          />
          <SelectComponent
            label={t('Source')}
            placeholder={t('Choose source')}
            value={form.filter.source}
            initOptions={[NOT_ASSIGN_ITEM]}
            isMulti
            loadOptions={getSourceList}
            onChangeOption={handleChangeFilters('source')}
          />
          <SelectComponent
            label={t('Experience')}
            value={form.filter.experience}
            initOptions={[NOT_ASSIGN_ITEM]}
            placeholder={t('Choose experience')}
            isMulti
            isSearchable={false}
            loadOptions={getPersonExperienceList}
            onChangeOption={handleChangeFilters('experience')}
          />
          <SelectComponent
            label={t('Region')}
            placeholder={t('Choose region')}
            value={form.filter.region}
            initOptions={[NOT_ASSIGN_ITEM]}
            isMulti
            isSearchable={false}
            additional={{ userRoleIds }}
            loadOptions={getRegionList}
            onChangeOption={handleChangeFilters('region')}
          />
          <SelectComponent
            label={t('Department')}
            placeholder={t('Choose department')}
            isMulti
            isSearchable={false}
            value={form.filter.department}
            initOptions={[NOT_ASSIGN_ITEM]}
            loadOptions={getDepartmentList}
            onChangeOption={handleChangeFilters('department')}
          />
          <SelectComponent
            label={t('English level')}
            value={form.filter.englishLevel}
            initOptions={[NOT_ASSIGN_ITEM]}
            placeholder={t('Choose english level')}
            isMulti
            isSearchable={false}
            loadOptions={getEnglishLevelList}
            onChangeOption={handleChangeFilters('englishLevel')}
          />
          <SelectComponent
            label={t('Referral')}
            placeholder={t('Choose referral')}
            isMulti
            value={form.filter.referral}
            initOptions={[NOT_ASSIGN_ITEM]}
            loadOptions={getEmployeeList}
            onChangeOption={handleChangeFilters('referral')}
          />
          <SelectComponent
            label={t('Tags')}
            value={form.filter.tags}
            placeholder={t('Choose tag(-s)')}
            isMulti
            loadOptions={getTagsList}
            onChangeOption={handleChangeFilters('tags')}
          />
          <InputComponent
            placeholder={t('Choose date')}
            label={t('Date')}
            onClearClick={() => handleChangeFilters('dateRange')(undefined)}
            onClick={() =>
              openModal(DateRangeModalComponent, {
                dateRange: form.filter.dateRange,
                handleChange: handleChangeFilters('dateRange'),
              })
            }
            value={formatDateRange}
          />
        </StyledFilterBlockFilters>

        <ButtonComponent appearance="primary" handleClick={handleApplyFilters}>
          {t('Show results')}
        </ButtonComponent>
        <ButtonComponent handleClick={handleResetFilters}>
          {t('Reset')}
        </ButtonComponent>
      </StyledFilterWrapper>

      <StyledCollapseLine onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </StyledCollapseLine>
    </StyledFilterBlock>
  );
};

export default CandidatesFiltersForm;
